/**
 * Families. The crew ledger: who is in, what they bring in, and what the family owns.
 * Header band + treasury + perks sit above a tab rail; the right rail is always live.
 *
 * NOTHING ON THIS SCREEN IS COMPUTED HERE. The roster, the two treasury tanks, the branch
 * prices, the perk lines and every refusal arrive from `/family` priced against this viewer at
 * this instant, and the socket only ever patches the slice that read produced. The screen draws
 * what it was handed and sends gestures back.
 *
 * There are two states and both are real. A player with no family gets the BROWSER and the
 * charter form. A member gets the board. There is no third state where the board is drawn out of
 * module-level constants, which is what this file used to be.
 */

import { useEffect, useMemo, useState } from 'react'
import type {
  Announcement,
  Application,
  Cents,
  ChatLine,
  Currency,
  FamilyAuditEntry,
  FamilyCrew,
  FamilyOverview,
  FamilyPermission,
  FamilyRole,
  FamilySummary,
  Member,
  PlayerStatus,
  RankRequirement,
  RankState,
  SponsorState,
  TreasuryEntry,
  Upgrade,
  VaultItem,
  WarEntry,
} from '@ashport/shared'
import { FAMILY_PERMISSIONS, FAMILY_ROLES, LINE_NAME } from '@ashport/shared'
import { LINE_ENERGY_BP, LINE_NERVE_POINTS } from '@ashport/shared/formulas'
import {
  cents,
  FAMILY_TAG_MAX,
  FAMILY_TAG_MIN,
  TITHE_MAX_PCT,
  TITHE_MIN_PCT,
} from '@ashport/shared/formulas'
import { A } from '../art'
import type { KnownAssetId } from '../art'
import { Icon, type IconName } from '../ui/Icon'
import { Art, Btn, Chip, Col, Face, Meter, Panel, PanelHead, Rail, Row, Stack, Stat, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { useSession } from '../game/session'
import { clock, exact, money, useGame } from '../game/state'

/* -------------------------------------------------------------------- keys */

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'members', label: 'Members' },
  { id: 'territory', label: 'Territory' },
  { id: 'vault', label: 'Vault' },
  { id: 'upgrades', label: 'Upgrades' },
  { id: 'warlog', label: 'War Log' },
] as const

type TabId = (typeof TABS)[number]['id']

/** EIGHT roles. There is no lieutenant: `shared/types.ts` explains where it went and why. */
const ROLE_C: Record<FamilyRole, string> = {
  boss: 'var(--gold)',
  underboss: 'var(--clean)',
  consigliere: 'var(--nerve)',
  capo: 'var(--dirty)',
  enforcer: 'var(--arrest)',
  driver: 'var(--police)',
  soldier: 'var(--ink-mid)',
  associate: 'var(--ink-dim)',
}

const ROLE_LABEL: Record<FamilyRole, string> = {
  boss: 'Boss',
  underboss: 'Underboss',
  consigliere: 'Consigliere',
  capo: 'Capo',
  enforcer: 'Enforcer',
  driver: 'Driver',
  soldier: 'Soldier',
  associate: 'Associate',
}

const STATUS_C: Record<PlayerStatus, string> = {
  online: 'var(--dirty)',
  away: 'var(--gold)',
  offline: 'var(--ink-faint)',
  hospital: 'var(--arrest)',
  jailed: 'var(--police)',
}

const STATUS_LABEL: Record<PlayerStatus, string> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
  hospital: 'Hospital',
  jailed: 'Jailed',
}

/** A key is not a seat. The dossier lists the ten grants in English, never as dotted ids. */
const PERM_LABEL: Record<FamilyPermission, string> = {
  'vault.view': 'Sees the vault',
  'vault.take': 'Takes from the vault',
  'upkeep.spend': 'Spends on upkeep',
  'territory.declare': 'Declares on ground',
  'war.answer': 'Answers a declaration',
  'captain.payoff': 'Pays off a captain',
  'market.list_family': 'Lists for the family',
  'roster.invite': 'Brings people in',
  'roster.remove': 'Puts people out',
  'commendation.award': 'Awards commendations',
}

const RESULT_C: Record<WarEntry['result'], string> = {
  won: 'var(--dirty)',
  lost: 'var(--arrest)',
  stalemate: 'var(--ink-dim)',
}

const RESULT_LABEL: Record<WarEntry['result'], string> = {
  won: 'Won',
  lost: 'Lost',
  stalemate: 'Stalemate',
}

/**
 * Crew, branch and perk icons arrive as plain strings, and `Icon` indexes a closed map: a name
 * outside it draws an empty path, which is a glyph nobody notices is missing. Resolving through
 * here means an unseeded icon reads as the families mark rather than as nothing at all.
 */
const GLYPHS = new Set<string>([
  'fist',
  'dirty',
  'clean',
  'crate',
  'eye',
  'market',
  'heat',
  'legend',
  'users',
  'lock',
  'contracts',
  'ledger',
  'territory',
  'rank',
  'target',
  'skull',
  'cross',
])

const glyph = (n: string): IconName => (GLYPHS.has(n) ? (n as IconName) : 'families')

/**
 * An id the family has not claimed yet comes down as the EMPTY STRING, and `A('')` has nothing to
 * work with: the sibling walk splits on dots and gives up when there are none, so an empty id
 * falls all the way through to the `ui.materials` board. Substituting here keeps the choice of
 * stand-in on the screen that knows what the slot is for.
 */
/*
 * Only the FALLBACK narrows. `id` is `o.headerId`, `who.roleArtId` and `v.icon`, which arrive off
 * the wire out of KV content the client never compiled against, so a union would reject them for
 * being honest. The fallback is the opposite: it is the literal this screen types in to cover the
 * empty case, so it is exactly the value `KnownAssetId` exists to police.
 */
const art = (id: string, fallback: KnownAssetId): string => A(id === '' ? fallback : id, fallback)

const ago = (ms: number, now: number): string => {
  const s = Math.max(0, (now - ms) / 1000)
  if (s < 90) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86_400)}d ago`
}

/** Boxes are typed in dollars. The wire is CENTS, everywhere, without exception. */
const toCents = (s: string): Cents => {
  const d = Number(s)
  return (Number.isFinite(d) && d > 0 ? Math.round(d * 100) : 0) as Cents
}

const inputStyle: React.CSSProperties = {
  minWidth: 0,
  padding: '6px 8px',
  background: '#05080a',
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  font: '400 11.5px/1 var(--f-body)',
  outline: 'none',
}

const selectStyle: React.CSSProperties = {
  appearance: 'none',
  padding: '5px 7px',
  background: '#05080a',
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  font: '400 11px/1 var(--f-body)',
  outline: 'none',
  cursor: 'pointer',
  minWidth: 0,
}

/* ------------------------------------------------------------------ fragments */

function Dot({ c }: { c: string }) {
  return <i style={{ width: 5, height: 5, borderRadius: '50%', background: c, flex: 'none' }} />
}

function RoleChip({ r }: { r: FamilyRole }) {
  return <Chip c={ROLE_C[r]}>{ROLE_LABEL[r]}</Chip>
}

/** A refusal, in voice, on the screen that caused it. Never a status code. */
function Refusal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Panel tone="danger" style={{ flex: 'none' }}>
      <PanelHead
        title="No"
        icon="warn"
        accent="var(--arrest)"
        right={
          <button
            onClick={onClose}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <p style={{ margin: 0, padding: '9px 11px 11px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

/**
 * The other half of `Refusal`: an authored line an action ANSWERED with.
 *
 * There was no such surface, which is why `content/house.json`'s `taken` shipped in the bundle,
 * arrived on the response to `POST /api/rank/house`, and was dropped by `act()` before any screen
 * could see it. The floor's whole payoff, the moment the house says on the record that it has
 * spoken for you, had nowhere to land.
 */
function Notice({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead
        title="On The Record"
        icon="rep"
        accent="var(--gold)"
        right={
          <button
            onClick={onClose}
            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            aria-label="Dismiss"
          >
            <Icon n="close" s={11} c="var(--ink-dim)" />
          </button>
        }
      />
      <p style={{ margin: 0, padding: '9px 11px 11px', fontSize: 12, color: 'var(--ink)' }}>{text}</p>
    </Panel>
  )
}

function Empty({ icon, head, sub }: { icon: IconName; head: string; sub: string }) {
  return (
    <div style={{ padding: '26px 12px', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
      <Icon n={icon} s={24} c="var(--ink-faint)" />
      <div className="t-head" style={{ fontSize: 11, color: 'var(--ink-dim)', letterSpacing: '.2em', marginTop: 8 }}>
        {head}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 3, maxWidth: 380, lineHeight: 1.45 }}>
        {sub}
      </div>
    </div>
  )
}

/**
 * The crest, or the monogram that stands in for one.
 *
 * Fifteen `families.crests.*` assets exist against a design that expects far more than fifteen
 * families, so `crestId` is the empty string for anybody who has not claimed one and the gold
 * monogram is what an uncrested door actually looks like. There is deliberately no plain steel
 * plate asset yet; it is escalated in docs/CONTENT-GAPS.md.
 */
function Crest({ crestId, name, size }: { crestId: string; name: string; size: number }) {
  // The first letter of the NAME, not of the article in front of it. "The Marchetti Crew" put a
  // T on the plate, which is nobody's initial, and the crest tile is the family's identity on
  // every screen it appears on.
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words.length > 1 && /^(the|a|an)$/i.test(words[0]) ? words[1] : words[0]
  const letter = (first ?? '').charAt(0).toUpperCase() || '?'
  if (crestId === '') {
    return (
      <div
        style={{
          width: size,
          height: size * 1.09,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid var(--line-brass)',
          background: 'radial-gradient(60% 60% at 50% 45%, rgba(0,0,0,.55), rgba(0,0,0,.85))',
        }}
      >
        <span
          className="t-display"
          style={{
            fontSize: size * 0.5,
            color: 'var(--gold)',
            textShadow: '0 3px 0 #000, 0 0 26px rgba(214,168,75,.55)',
          }}
        >
          {letter}
        </span>
      </div>
    )
  }
  return (
    <Art
      src={A(crestId)}
      alt={`${name} crest`}
      scrim="none"
      style={{
        width: size,
        height: size * 1.09,
        flex: 'none',
        border: '1px solid var(--line-brass)',
        filter: 'sepia(.5) saturate(1.2) contrast(1.05)',
      }}
    />
  )
}

/* ------------------------------------------------------------------- header */

function HeaderBand({ o, now }: { o: FamilyOverview; now: number }) {
  return (
    <Panel tone="gold" style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      <Art
        src={art(o.headerId, 'brand.skyline')}
        alt={`The ${o.name} hall on the Ashport waterfront at night`}
        scrim="soft"
        focal="62% 46%"
        style={{ position: 'absolute', inset: 0 }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(5,7,9,.93) 0%, rgba(5,7,9,.72) 46%, rgba(5,7,9,.18) 100%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          padding: '10px 14px 12px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <div className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-dim)' }}>
          Family / {o.name}
        </div>
        <Row gap={16} style={{ flex: 1, marginTop: 4 }}>
          <Crest crestId={o.crestId} name={o.name} size={108} />

          <div style={{ minWidth: 0, flex: 1 }}>
            <Row gap={9}>
              <div
                className="t-display"
                style={{
                  fontSize: 42,
                  letterSpacing: '.03em',
                  background: 'linear-gradient(178deg,#f6e2b0 6%,#d6a84b 44%,#8a6524 92%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  filter: 'drop-shadow(0 2px 0 #000)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {o.name}
              </div>
              <Chip c="var(--brass-lit)">{o.tag}</Chip>
            </Row>
            <div
              style={{
                marginTop: 6,
                fontSize: 12.5,
                color: 'var(--ink-mid)',
                fontStyle: 'italic',
                textShadow: '0 1px 6px #000',
              }}
            >
              {o.motto === '' ? 'No motto over the door yet.' : <>&ldquo;{o.motto}&rdquo;</>}
            </div>
            <Row gap={14} style={{ marginTop: 10, flexWrap: 'wrap' }}>
              <Row gap={5}>
                <Icon n="clock" s={11} c="var(--ink-dim)" />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Founded</span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-hi)' }}>
                  {ago(o.foundedAt, now)}
                </span>
              </Row>
              <Row gap={5}>
                <Icon n="users" s={11} c="var(--ink-dim)" />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Members</span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-hi)' }}>
                  {o.memberCount} / {o.memberCap}
                </span>
              </Row>
              <Row gap={5}>
                <Icon n="crown" s={11} c="var(--gold)" />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Boss</span>
                <span style={{ fontSize: 11, color: 'var(--ink-hi)' }}>{o.bossName}</span>
              </Row>
              <Row gap={5}>
                <Icon n="rank" s={11} c="var(--brass-lit)" />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Power</span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--gold)' }}>
                  {o.power.toLocaleString('en-US')}
                </span>
              </Row>
            </Row>
          </div>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * Two tanks, never one.
 *
 * The tithe deposits DIRTY and every family sink spends CLEAN, so a single balance cannot say
 * which of the two a withdrawal took. The old panel also counted down to a dues collection that
 * does not exist: `DUES_AT` was `Date.now() + 21240000` read off the browser clock at module
 * load, which is a countdown to nothing at all.
 */
function TreasuryPanel({
  o,
  busy,
  onDeposit,
  onWithdraw,
}: {
  o: FamilyOverview
  busy: boolean
  onDeposit: (cur: Currency, amount: Cents) => void
  onWithdraw: (cur: Currency, amount: Cents) => void
}) {
  const [cur, setCur] = useState<Currency>('dirty')
  const [amt, setAmt] = useState('')
  const amount = toCents(amt)
  const held = (o.treasuryDirty + o.treasuryClean) as Cents

  return (
    <Panel style={{ width: 196, flex: 'none' }}>
      <PanelHead title="Family Treasury" icon="dirty" accent="var(--dirty)" />
      <div style={{ padding: '8px 11px 9px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div className="t-label" style={{ fontSize: sz(8.5), color: 'var(--dirty)' }}>Dirty cash</div>
        <div className="t-num" style={{ fontSize: 18, color: 'var(--dirty)' }}>{exact(o.treasuryDirty)}</div>
        <div className="t-label" style={{ fontSize: sz(8.5), color: 'var(--clean)', marginTop: 5 }}>Clean cash</div>
        <div className="t-num" style={{ fontSize: 18, color: 'var(--clean)' }}>{exact(o.treasuryClean)}</div>
        <div style={{ marginTop: 7 }}>
          <Meter v={held} max={o.treasuryCap} a="var(--brass-deep)" b="var(--gold)" h={4} />
        </div>
        <Row style={{ marginTop: 4, justifyContent: 'space-between' }}>
          <span className="t-label" style={{ fontSize: sz(8) }}>Cap</span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-mid)' }}>
            {money(held)} / {money(o.treasuryCap)}
          </span>
        </Row>
        <Row style={{ marginTop: 2, justifyContent: 'space-between' }}>
          <span className="t-label" style={{ fontSize: sz(8) }}>Automatic cut</span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--gold)' }}>{o.tithePct}% of dirty</span>
        </Row>
        <div style={{ flex: 1 }} />
        <Row gap={4} style={{ marginTop: 7 }}>
          <select
            value={cur}
            onChange={(e) => setCur(e.target.value as Currency)}
            style={{ ...selectStyle, width: 58 }}
          >
            <option value="dirty">Dirty</option>
            <option value="clean">Clean</option>
          </select>
          <input
            value={amt}
            onChange={(e) => setAmt(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="amount"
            style={{ ...inputStyle, flex: 1, textAlign: 'right' }}
          />
        </Row>
        <Row gap={4} style={{ marginTop: 5 }}>
          <Btn
            v="gold"
            size="sm"
            icon="plus"
            disabled={busy || amount <= 0}
            onClick={() => onDeposit(cur, amount)}
            style={{ flex: 1 }}
          >
            Pay In
          </Btn>
          <Btn
            v="ghost"
            size="sm"
            icon="minus"
            disabled={busy || amount <= 0}
            onClick={() => onWithdraw(cur, amount)}
            style={{ flex: 1 }}
          >
            Take
          </Btn>
        </Row>
      </div>
    </Panel>
  )
}

/**
 * The perks a family actually has, which is none until it takes a branch.
 *
 * The mockup shipped six invented lines here, none of which mapped to a mechanic. The server
 * derives this list from the branches bought, so an empty panel is the truthful one.
 */
function PerksPanel({ o }: { o: FamilyOverview }) {
  return (
    <Panel style={{ width: 224, flex: 'none' }}>
      <PanelHead title="Family Perks" icon="legend" accent="var(--gold)" sub={`${o.perks.length} live`} />
      <div style={{ padding: '7px 10px 9px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Col gap={4} className="scroll-y" style={{ flex: 1, minHeight: 0 }}>
          {o.perks.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ink-faint)', lineHeight: 1.45 }}>
              Nothing yet. A crew takes two of the four branches and lives with them, and the perks
              are whatever those two turn out to be.
            </div>
          ) : (
            o.perks.map((p) => (
              <Row key={p.text} gap={7} style={{ alignItems: 'flex-start' }}>
                <Icon n={glyph(p.icon)} s={12} c={p.c} style={{ marginTop: 2 }} />
                <span style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.25 }}>{p.text}</span>
              </Row>
            ))
          )}
        </Col>
        <div className="rule" style={{ margin: '7px 0 6px' }} />
        <Row style={{ justifyContent: 'space-between' }}>
          <Stat label="Books" value={o.books} />
          <Stat label="Sponsored" value={o.sponsoredCount} align="right" />
        </Row>
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ overview */

function SeatCard({ m, label, onPick }: { m: Member | undefined; label: string; onPick: (id: string) => void }) {
  if (!m) {
    return (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          border: '1px dashed var(--line)',
          background: 'rgba(255,255,255,.015)',
        }}
      >
        <div
          className="t-head"
          style={{
            fontSize: sz(9.5),
            textAlign: 'center',
            padding: '4px 0 3px',
            color: 'var(--ink-faint)',
            borderBottom: '1px solid var(--line-soft)',
            letterSpacing: '.2em',
          }}
        >
          {label}
        </div>
        <div style={{ padding: '14px 9px', fontSize: 11, color: 'var(--ink-faint)', textAlign: 'center' }}>
          The seat is empty.
        </div>
      </div>
    )
  }
  return (
    <button
      onClick={() => onPick(m.playerId)}
      style={{
        flex: 1,
        minWidth: 0,
        padding: 0,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        border: `1px solid ${m.me ? 'var(--line-brass)' : 'var(--line)'}`,
        background: m.me
          ? 'linear-gradient(180deg, rgba(214,168,75,.12), rgba(8,11,13,.6))'
          : 'linear-gradient(180deg, rgba(255,255,255,.03), rgba(8,11,13,.6))',
      }}
    >
      <div
        className="t-head"
        style={{
          fontSize: sz(9.5),
          textAlign: 'center',
          padding: '4px 0 3px',
          color: ROLE_C[m.role],
          borderBottom: '1px solid var(--line-soft)',
          letterSpacing: '.2em',
        }}
      >
        {label}
      </div>
      <Row gap={9} style={{ padding: '8px 9px' }}>
        <Face src={art(m.portraitId, 'ui.avatar.socket_square')} alt={m.name} size={44} ring />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11.5,
              color: m.me ? 'var(--gold)' : 'var(--ink-hi)',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {m.name}
          </div>
          <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)', marginTop: 1 }}>
            Level {m.level}
          </div>
          <Row gap={4} style={{ marginTop: 3 }}>
            <Dot c={STATUS_C[m.status]} />
            <span style={{ fontSize: sz(9.5), color: STATUS_C[m.status] }}>{STATUS_LABEL[m.status]}</span>
          </Row>
        </div>
      </Row>
    </button>
  )
}

function HierarchyPanel({ roster, onPick }: { roster: Member[]; onPick: (id: string) => void }) {
  const seat = (r: FamilyRole) => roster.find((m) => m.role === r)
  const capos = roster.filter((m) => m.role === 'capo')
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="Family Hierarchy" icon="rank" sub="The table" />
      <div style={{ padding: '9px 10px 10px' }}>
        <Row gap={8} style={{ alignItems: 'stretch' }}>
          <SeatCard m={seat('boss')} label="Boss" onPick={onPick} />
          <SeatCard m={seat('underboss')} label="Underboss" onPick={onPick} />
          <SeatCard m={seat('consigliere')} label="Consigliere" onPick={onPick} />
        </Row>
        <div
          className="t-head"
          style={{
            fontSize: sz(9),
            color: 'var(--brass-lit)',
            letterSpacing: '.24em',
            textAlign: 'center',
            margin: '9px 0 6px',
          }}
        >
          Capos
        </div>
        {capos.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', textAlign: 'center', padding: '4px 0 2px' }}>
            No capos yet. Nobody is running a crew.
          </div>
        ) : (
          <Row gap={6} style={{ alignItems: 'stretch', flexWrap: 'wrap' }}>
            {capos.map((c) => (
              <button
                key={c.playerId}
                onClick={() => onPick(c.playerId)}
                style={{
                  flex: 1,
                  minWidth: 130,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '5px 6px',
                  textAlign: 'left',
                  background: c.me ? 'rgba(214,168,75,.1)' : 'rgba(255,255,255,.025)',
                  border: `1px solid ${c.me ? 'var(--line-brass)' : 'var(--line)'}`,
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <Face src={art(c.portraitId, 'ui.avatar.socket_square')} alt={c.name} size={30} />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: sz(10.5),
                      color: c.me ? 'var(--gold)' : 'var(--ink-hi)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.name}
                  </div>
                  <Row gap={5}>
                    <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-dim)' }}>
                      Lv {c.level}
                    </span>
                    <Dot c={STATUS_C[c.status]} />
                  </Row>
                </div>
              </button>
            ))}
          </Row>
        )}
      </div>
    </Panel>
  )
}

function RosterTable({
  rows,
  sel,
  onSel,
  now,
  dense,
}: {
  rows: Member[]
  sel: string | null
  onSel: (id: string) => void
  now: number
  dense?: boolean
}) {
  return (
    <table className="grid-tbl">
      <thead>
        <tr>
          {['Member', 'Role', 'Lvl', 'Status', 'Crew Assignment', 'Weekly', 'XP'].map((h, i) => (
            <th
              key={h}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: 'var(--panel)',
                textAlign: i >= 5 ? 'right' : i === 2 ? 'center' : 'left',
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => (
          <tr
            key={m.playerId}
            data-on={sel === m.playerId || undefined}
            onClick={() => onSel(m.playerId)}
            style={{ cursor: 'pointer' }}
          >
            <td style={{ padding: dense ? '4px 10px' : '6px 10px' }}>
              <Row gap={8}>
                <Face src={art(m.portraitId, 'ui.avatar.socket_square')} alt={m.name} size={dense ? 22 : 26} ring={m.me} />
                <span
                  style={{
                    fontSize: 11.5,
                    color: m.me ? 'var(--gold)' : 'var(--ink-hi)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m.name}
                  {m.me && <span style={{ color: 'var(--ink-dim)' }}> (You)</span>}
                </span>
                {m.probationUntil > now && (
                  <span className="t-head" style={{ fontSize: sz(8.5), color: 'var(--police)', letterSpacing: '.14em' }}>
                    On Ice
                  </span>
                )}
                {m.sponsored && <Icon n="rep" s={10} c="var(--gold)" />}
              </Row>
            </td>
            <td>
              <span className="t-head" style={{ fontSize: sz(9.5), color: ROLE_C[m.role], letterSpacing: '.14em' }}>
                {ROLE_LABEL[m.role]}
              </span>
            </td>
            <td className="t-num" style={{ textAlign: 'center', color: 'var(--ink-hi)' }}>{m.level}</td>
            <td>
              <Row gap={5}>
                <Dot c={STATUS_C[m.status]} />
                <span style={{ fontSize: sz(10.5), color: STATUS_C[m.status] }}>{STATUS_LABEL[m.status]}</span>
              </Row>
            </td>
            <td style={{ fontSize: 11, color: 'var(--ink-mid)', whiteSpace: 'nowrap' }}>
              {m.crew === '' ? 'No crew' : m.crew}
            </td>
            <td className="t-num" style={{ textAlign: 'right', color: 'var(--dirty)', whiteSpace: 'nowrap' }}>
              {exact(m.weekly)}
            </td>
            <td className="t-num" style={{ textAlign: 'right', color: 'var(--ink-mid)', whiteSpace: 'nowrap' }}>
              {m.xp.toLocaleString('en-US')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ApplicationsPanel({
  rows,
  busy,
  now,
  onDecide,
}: {
  rows: Application[]
  busy: boolean
  now: number
  onDecide: (playerId: string, accept: boolean) => void
}) {
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title="Applications Inbox"
        icon="mail"
        right={
          <span
            className="t-num"
            style={{
              minWidth: 18,
              height: 16,
              padding: '0 5px',
              display: 'grid',
              placeItems: 'center',
              fontSize: sz(10),
              color: '#16110a',
              background: rows.length ? 'var(--gold)' : 'var(--ink-faint)',
            }}
          >
            {rows.length}
          </span>
        }
      />
      <div style={{ padding: '7px 9px 9px' }}>
        {rows.length === 0 ? (
          <Empty icon="mail" head="Nobody is knocking" sub="Applications land here the moment somebody asks in." />
        ) : (
          <Col gap={5}>
            {rows.map((a) => (
              <Row
                key={a.playerId}
                gap={8}
                style={{
                  padding: '6px 7px',
                  border: '1px solid var(--line)',
                  background: 'rgba(255,255,255,.02)',
                  alignItems: 'flex-start',
                }}
              >
                <Face src={art(a.portraitId, 'ui.avatar.socket_square')} alt={a.name} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={7}>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{a.name}</span>
                    <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-dim)' }}>
                      Level {a.level}
                    </span>
                    <div style={{ flex: 1 }} />
                    <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-faint)' }}>
                      {ago(a.appliedAt, now)}
                    </span>
                  </Row>
                  <div style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.35 }}>{a.pitch}</div>
                  <Row gap={6}>
                    <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--ink-faint)' }}>
                      {a.referral}
                    </span>
                    <span className="t-mono" style={{ fontSize: sz(9), color: 'var(--ink-faint)' }}>
                      lapses in {clock((a.expiresAt - now) / 1000)}
                    </span>
                  </Row>
                </div>
                <Row gap={4} style={{ flex: 'none', alignSelf: 'center' }}>
                  <Btn v="safe" size="sm" icon="check" disabled={busy} onClick={() => onDecide(a.playerId, true)} />
                  <Btn v="danger" size="sm" icon="close" disabled={busy} onClick={() => onDecide(a.playerId, false)} />
                </Row>
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

function CrewStructurePanel({ crews }: { crews: FamilyCrew[] }) {
  const staffed = crews.reduce((s, c) => s + c.on, 0)
  const cap = crews.reduce((s, c) => s + c.cap, 0)
  return (
    <Panel style={{ flex: 1, minWidth: 0 }}>
      <PanelHead title="Crew Structure" icon="users" sub={`${staffed} / ${cap} staffed`} />
      <div className="scroll-y" style={{ flex: 1, padding: '7px 10px 10px' }}>
        <Col gap={7}>
          {crews.map((c) => (
            <div key={c.crewId}>
              <Row gap={7}>
                <Icon n={glyph(c.icon)} s={12} c={c.c} />
                <span
                  style={{
                    fontSize: 11.5,
                    color: 'var(--ink)',
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {c.name}
                </span>
                <span
                  className="t-mono"
                  style={{ fontSize: sz(10.5), color: c.on >= c.cap ? 'var(--dirty)' : 'var(--ink-mid)' }}
                >
                  {c.on} / {c.cap}
                </span>
              </Row>
              <div style={{ marginTop: 3 }}>
                <Meter v={c.on} max={c.cap} a="var(--brass-deep)" b={c.c} h={4} />
              </div>
              <div className="t-label" style={{ fontSize: sz(8), marginTop: 2, color: 'var(--ink-faint)' }}>
                {c.lead === '' ? 'No capo on it' : `Lead ${c.lead}`}
              </div>
            </div>
          ))}
        </Col>
      </div>
    </Panel>
  )
}

function AnnouncementsPanel({ rows, now }: { rows: Announcement[]; now: number }) {
  return (
    <Panel style={{ flex: 1, minWidth: 0 }}>
      <PanelHead title="Announcements" icon="info" />
      <div className="scroll-y" style={{ flex: 1, padding: '7px 10px 10px' }}>
        {rows.length === 0 ? (
          <Empty icon="info" head="Nothing posted" sub="A notice from the table shows up here and in everybody's rail." />
        ) : (
          <Col gap={8}>
            {rows.map((a) => (
              <Row key={a.id} gap={8} style={{ alignItems: 'flex-start' }}>
                <Face src={art(a.portraitId, 'ui.avatar.socket_square')} alt={a.who} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={6}>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--gold)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {a.who}
                    </span>
                    {a.pinned && <Icon n="pin" s={10} c="var(--brass-lit)" />}
                    <div style={{ flex: 1 }} />
                    <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-faint)' }}>
                      {ago(a.at, now)}
                    </span>
                  </Row>
                  <div style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.4 }}>{a.body}</div>
                </div>
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------- rail */

type FeedRow = { id: string; at: number; kind: 'money' | 'log'; head: string; sub: string; c: string; icon: IconName }

/**
 * The real feed.
 *
 * Two recorded streams and nothing else: the audit log, which writes a row for every role,
 * permission and roster change forever, and the treasury ledger, which writes one for every cent
 * that moves. The invented "activity" list this replaced had eight events with faces and payouts
 * that no system ever produced.
 */
function FeedPanel({ audit, log, now }: { audit: FamilyAuditEntry[]; log: TreasuryEntry[]; now: number }) {
  const [filter, setFilter] = useState<'all' | 'money' | 'log'>('all')

  const rows = useMemo<FeedRow[]>(() => {
    const a: FeedRow[] = audit.map((e) => ({
      id: `a-${e.id}`,
      at: e.at,
      kind: 'log',
      head: `${e.actor} ${e.action}`,
      sub: [e.subject, e.detail].filter((s) => s !== '').join(' · '),
      c: 'var(--ink-mid)',
      icon: 'ledger',
    }))
    const m: FeedRow[] = log.map((e) => ({
      id: `t-${e.id}`,
      at: e.at,
      kind: 'money',
      head: `${e.who} ${e.delta >= 0 ? 'paid in' : 'took out'}`,
      sub: `${exact(Math.abs(e.delta) as Cents)} ${e.currency}${e.reason === '' ? '' : ` · ${e.reason}`}`,
      c: e.delta >= 0 ? 'var(--dirty)' : 'var(--arrest)',
      icon: e.currency === 'clean' ? 'clean' : 'dirty',
    }))
    return [...a, ...m].sort((x, y) => y.at - x.at).slice(0, 60)
  }, [audit, log])

  const shown = rows.filter((r) => filter === 'all' || r.kind === filter)

  return (
    <Panel style={{ flex: '1 1 0' }}>
      <PanelHead
        title="Family Record"
        icon="scanner"
        right={
          <Row gap={3}>
            {(['all', 'money', 'log'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="t-head"
                style={{
                  padding: '2px 6px',
                  fontSize: sz(8.5),
                  letterSpacing: '.14em',
                  color: filter === f ? 'var(--gold)' : 'var(--ink-dim)',
                  background: filter === f ? 'rgba(214,168,75,.12)' : 'transparent',
                  border: `1px solid ${filter === f ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                  cursor: 'pointer',
                }}
              >
                {f}
              </button>
            ))}
          </Row>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '6px 9px' }}>
        {shown.length === 0 ? (
          <Empty icon="scanner" head="Nothing recorded" sub="Every grant, every kick and every cent writes a line here." />
        ) : (
          <Col gap={6}>
            {shown.map((r) => (
              <Row key={r.id} gap={8} style={{ alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    border: '1px solid var(--line)',
                    background: 'rgba(255,255,255,.03)',
                  }}
                >
                  <Icon n={r.icon} s={13} c={r.c} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={6} style={{ alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-hi)', lineHeight: 1.3, flex: 1 }}>{r.head}</span>
                    <span className="t-mono" style={{ fontSize: sz(9), color: 'var(--ink-faint)', flex: 'none' }}>
                      {ago(r.at, now)}
                    </span>
                  </Row>
                  {r.sub !== '' && <div style={{ fontSize: sz(10.5), color: r.c, lineHeight: 1.3 }}>{r.sub}</div>}
                </div>
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

/**
 * Family chat, over the room socket.
 *
 * The header says whether the LINE is up, which is a fact this client holds, rather than how many
 * people are online, which it does not: every deploy kills every socket and a presence count with
 * nothing behind it would be wrong for minutes at a time without anybody noticing.
 */
function ChatPanel({
  lines,
  busy,
  status,
  now,
  onSend,
}: {
  lines: ChatLine[]
  busy: boolean
  status: 'connecting' | 'open' | 'closed'
  now: number
  onSend: (body: string) => Promise<boolean>
}) {
  const [draft, setDraft] = useState('')
  const live = status === 'open'

  const fire = () => {
    const body = draft.trim()
    if (body === '' || busy) return
    void onSend(body).then((ok) => {
      // Cleared only on success, so a refused line is still in the box to fix and send again.
      if (ok) setDraft('')
    })
  }

  return (
    <Panel style={{ flex: '1 1 0' }}>
      <PanelHead
        title="Family Chat"
        icon="users"
        right={
          <Row gap={5}>
            <Dot c={live ? 'var(--dirty)' : 'var(--gold)'} />
            <span className="t-mono" style={{ fontSize: sz(10), color: live ? 'var(--dirty)' : 'var(--gold)' }}>
              {live ? 'live' : 'reconnecting'}
            </span>
          </Row>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '7px 9px' }}>
        {lines.length === 0 ? (
          <Empty icon="users" head="Quiet room" sub="Nothing said yet. Say the first thing." />
        ) : (
          <Col gap={8}>
            {lines.map((c) => (
              <Row key={c.seq} gap={8} style={{ alignItems: 'flex-start' }}>
                <Face src={art(c.portraitId, 'ui.avatar.socket_square')} alt={c.who} size={26} round />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={6}>
                    <span style={{ fontSize: 11, color: c.me ? 'var(--gold)' : 'var(--ink-hi)', fontWeight: 500 }}>
                      {c.who}
                    </span>
                    <RoleChip r={c.role} />
                    <div style={{ flex: 1 }} />
                    <span className="t-mono" style={{ fontSize: sz(9), color: 'var(--ink-faint)' }}>{ago(c.at, now)}</span>
                  </Row>
                  <div style={{ fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.4 }}>{c.body}</div>
                </div>
              </Row>
            ))}
          </Col>
        )}
      </div>
      <Row gap={0} style={{ padding: '7px 8px', borderTop: '1px solid var(--line-soft)' }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') fire()
          }}
          placeholder="Say something..."
          disabled={busy}
          style={{ ...inputStyle, flex: 1, font: '400 12px/1 var(--f-body)' }}
        />
        <button
          onClick={fire}
          disabled={busy || draft.trim() === ''}
          aria-label="Send"
          style={{
            width: 32,
            height: 29,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(180deg,#2a353d,#151d22)',
            border: '1px solid var(--line)',
            borderLeft: 'none',
            cursor: busy || draft.trim() === '' ? 'default' : 'pointer',
          }}
        >
          <Icon n="send" s={13} c={busy || draft.trim() === '' ? 'var(--ink-faint)' : 'var(--gold)'} />
        </button>
      </Row>
    </Panel>
  )
}

/**
 * Only the actions that exist.
 *
 * The mock's grid offered six, of which one had an endpoint behind it. Bulk Message, Set Quotas
 * and Create Crew are not systems, so they are not buttons. Whether this viewer may do any of
 * these is the server's call: it refuses in voice and the rail shows the refusal.
 */
function QuickActions({
  o,
  busy,
  onAnnounce,
  onTithe,
  onRecruiting,
  onLeave,
}: {
  o: FamilyOverview
  busy: boolean
  onAnnounce: (body: string) => Promise<boolean>
  onTithe: (pct: number) => void
  onRecruiting: (on: boolean) => void
  onLeave: () => void
}) {
  const [notice, setNotice] = useState('')
  const [pct, setPct] = useState(String(o.tithePct))

  const post = () => {
    const body = notice.trim()
    if (body === '' || busy) return
    void onAnnounce(body).then((ok) => {
      if (ok) setNotice('')
    })
  }

  const wanted = Number(pct)
  const titheOk =
    Number.isFinite(wanted) && wanted >= TITHE_MIN_PCT && wanted <= TITHE_MAX_PCT && wanted !== o.tithePct

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="The Table" icon="gear" />
      <Col gap={7} style={{ padding: '8px 9px 10px' }}>
        <Row gap={4}>
          <input
            value={notice}
            onChange={(e) => setNotice(e.target.value)}
            placeholder="Post a notice"
            disabled={busy}
            style={{ ...inputStyle, flex: 1 }}
          />
          <Btn size="sm" v="gold" disabled={busy || notice.trim() === ''} onClick={post}>
            Post
          </Btn>
        </Row>
        <Row gap={4}>
          <span className="t-label" style={{ fontSize: sz(8.5), flex: 1 }}>
            Tithe, {TITHE_MIN_PCT} to {TITHE_MAX_PCT} percent
          </span>
          <input
            value={pct}
            onChange={(e) => setPct(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ ...inputStyle, width: 46, textAlign: 'right' }}
          />
          <Btn size="sm" v="ghost" disabled={busy || !titheOk} onClick={() => onTithe(wanted)}>
            Set
          </Btn>
        </Row>
        <Btn
          size="sm"
          v={o.recruiting ? 'ghost' : 'police'}
          full
          icon="users"
          disabled={busy}
          onClick={() => onRecruiting(!o.recruiting)}
        >
          {o.recruiting ? 'Close The Door' : 'Open The Door'}
        </Btn>
        <Btn size="sm" v="danger" full icon="close" disabled={busy} onClick={onLeave}>
          Leave Family
        </Btn>
      </Col>
    </Panel>
  )
}

/* ------------------------------------------------------------------- tabs */

function OverviewTab({
  o,
  roster,
  applications,
  announcements,
  busy,
  now,
  onSel,
  onDecide,
}: {
  o: FamilyOverview
  roster: Member[]
  applications: Application[]
  announcements: Announcement[]
  busy: boolean
  now: number
  onSel: (id: string) => void
  onDecide: (playerId: string, accept: boolean) => void
}) {
  const weekly = roster.reduce((s, m) => s + m.weekly, 0) as Cents
  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
      <Col gap={8} style={{ flex: 1.35, minWidth: 0 }}>
        <HierarchyPanel roster={roster} onPick={onSel} />
        <Panel style={{ flex: 1, minHeight: 0 }}>
          <PanelHead
            title="Family Roster"
            icon="families"
            sub={`${o.memberCount} members`}
            right={
              <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--dirty)' }}>
                {money(weekly)} tithed this week
              </span>
            }
          />
          <div className="scroll-y" style={{ flex: 1 }}>
            <RosterTable rows={roster} sel={null} onSel={onSel} now={now} dense />
          </div>
        </Panel>
      </Col>
      <Col gap={8} style={{ flex: 1, minWidth: 0 }}>
        <ApplicationsPanel rows={applications} busy={busy} now={now} onDecide={onDecide} />
        <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
          <CrewStructurePanel crews={o.crews} />
          <AnnouncementsPanel rows={announcements} now={now} />
        </Row>
      </Col>
    </Row>
  )
}

/**
 * The dossier, which is now made of fields the server sent.
 *
 * The mock printed "Quota met 140%" from `Math.min(180, 60 + (level % 12) * 10)`, which is
 * arithmetic on a level wearing the clothes of a metric. What replaced it is the member's own
 * record: the seat, the crew, the week's tithed contribution, probation, the sponsorship and the
 * keys they hold.
 */
function Dossier({
  who,
  crews,
  vouchLocked,
  busy,
  now,
  onRole,
  onCrew,
  onGift,
  onVouch,
  onKick,
  onPermission,
}: {
  who: Member
  crews: FamilyCrew[]
  vouchLocked: string | null
  busy: boolean
  now: number
  onRole: (playerId: string, role: FamilyRole) => void
  onCrew: (playerId: string, crewId: string | null) => void
  onGift: (playerId: string) => void
  onVouch: (playerId: string) => void
  onKick: (playerId: string) => void
  /** Absent for a viewer who cannot hand out keys, which is what makes the row read-only. */
  onPermission?: (playerId: string, permission: FamilyPermission, grant: boolean) => void
}) {
  return (
    <Panel style={{ width: 290, flex: 'none' }}>
      <PanelHead title="Member Dossier" icon="contracts" accent="var(--gold)" />
      <div className="scroll-y" style={{ flex: 1, padding: '10px 11px' }}>
        <Row gap={10}>
          <Face src={art(who.portraitId, 'ui.avatar.socket_square')} alt={who.name} size={64} ring />
          <div style={{ minWidth: 0 }}>
            <div
              className="t-head"
              style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.06em', lineHeight: 1.25 }}
            >
              {who.name}
            </div>
            <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)', marginTop: 2 }}>
              {who.handle}
            </div>
            <Row gap={6} style={{ marginTop: 4 }}>
              <RoleChip r={who.role} />
              <Chip c={STATUS_C[who.status]}>{STATUS_LABEL[who.status]}</Chip>
            </Row>
          </div>
        </Row>
        <Row gap={7} style={{ marginTop: 9, alignItems: 'flex-start' }}>
          <Art
            src={art(who.roleArtId, 'families.roles.soldier')}
            alt={`${ROLE_LABEL[who.role]} insignia`}
            scrim="none"
            style={{ width: 38, height: 38, flex: 'none', border: '1px solid var(--line-soft)' }}
          />
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.45 }}>
            {who.note === '' ? 'Nothing on the sheet yet.' : who.note}
          </p>
        </Row>
        <div className="rule" style={{ margin: '10px 0' }} />
        <Row style={{ justifyContent: 'space-between' }}>
          <Stat label="Level" value={who.level} />
          <Stat label="Weekly" value={money(who.weekly)} c="var(--dirty)" align="center" />
          <Stat label="XP" value={who.xp.toLocaleString('en-US')} align="right" />
        </Row>
        <div className="rule" style={{ margin: '10px 0' }} />
        <Row style={{ justifyContent: 'space-between' }}>
          <span className="t-label" style={{ fontSize: sz(9) }}>Crew</span>
          <span style={{ fontSize: 11.5, color: 'var(--ink-hi)' }}>{who.crew === '' ? 'No crew' : who.crew}</span>
        </Row>
        <Row style={{ justifyContent: 'space-between', marginTop: 4 }}>
          <span className="t-label" style={{ fontSize: sz(9) }}>Joined</span>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-hi)' }}>{who.since}</span>
        </Row>
        <Row style={{ justifyContent: 'space-between', marginTop: 4 }}>
          <span className="t-label" style={{ fontSize: sz(9) }}>Probation</span>
          <span
            className="t-mono"
            style={{ fontSize: 11, color: who.probationUntil > now ? 'var(--police)' : 'var(--ink-mid)' }}
          >
            {who.probationUntil > now ? `On Ice ${clock((who.probationUntil - now) / 1000)}` : 'Served'}
          </span>
        </Row>
        <Row style={{ justifyContent: 'space-between', marginTop: 4 }}>
          <span className="t-label" style={{ fontSize: sz(9) }}>Sponsored</span>
          <span className="t-mono" style={{ fontSize: 11, color: who.sponsored ? 'var(--gold)' : 'var(--ink-mid)' }}>
            {who.sponsored ? 'Vouched for' : 'Walked in'}
          </span>
        </Row>
        <div className="rule" style={{ margin: '10px 0' }} />
        <div className="t-label" style={{ fontSize: sz(9) }}>Keys held</div>
        {/*
          * THE KEYS ARE HANDED OUT HERE, and until now they were a read-only chip row.
          *
          * `grantPermission` has been on the store and `POST /api/family/grant` on the server since
          * Phase 6, and `MembersTab` wired `onRole`, `onCrew`, `onGift`, `onVouch` and `onKick` and
          * no `onPermission`. So the whole ten-key delegation model was server-side only and the
          * one control a Boss actually had was the blunt one: change somebody's seat.
          *
          * That is backwards for the system it belongs to. A seat is public and permanent-ish; a
          * key is the thing you hand to one person for one job and take back afterwards. Without
          * this, a Boss who wanted somebody to touch the vault had to promote them.
          *
          * Every toggle is audit logged by the server, which is why they can be handed out freely.
          */}
        {onPermission === undefined ? (
          who.permissions.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 4 }}>None. A seat is not a key.</div>
          ) : (
            <Row gap={4} style={{ marginTop: 5, flexWrap: 'wrap' }}>
              {who.permissions.map((p) => (
                <Chip key={p} c="var(--clean)">{PERM_LABEL[p]}</Chip>
              ))}
            </Row>
          )
        ) : (
          <Row gap={4} style={{ marginTop: 5, flexWrap: 'wrap' }}>
            {FAMILY_PERMISSIONS.map((p) => {
              const held = who.permissions.includes(p)
              return (
                <button
                  key={p}
                  disabled={busy}
                  onClick={() => onPermission(who.playerId, p, !held)}
                  title={held ? 'Take this key back' : 'Hand this key over'}
                  style={{
                    font: '400 10px/1.2 var(--f-body)',
                    padding: '2px 7px',
                    cursor: busy ? 'default' : 'pointer',
                    color: held ? 'var(--clean)' : 'var(--ink-dim)',
                    background: held ? 'rgba(122,183,138,.10)' : 'transparent',
                    border: `1px solid ${held ? 'var(--clean)' : 'var(--line)'}`,
                  }}
                >
                  {PERM_LABEL[p]}
                </button>
              )
            })}
          </Row>
        )}
        <div className="rule" style={{ margin: '10px 0' }} />
        <div className="t-label" style={{ fontSize: sz(9) }}>Seat</div>
        <Row gap={4} style={{ marginTop: 5 }}>
          <select
            value={who.role}
            onChange={(e) => onRole(who.playerId, e.target.value as FamilyRole)}
            disabled={busy}
            style={{ ...selectStyle, flex: 1 }}
          >
            {FAMILY_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
          <select
            value={who.crewId ?? ''}
            onChange={(e) => onCrew(who.playerId, e.target.value === '' ? null : e.target.value)}
            disabled={busy}
            style={{ ...selectStyle, flex: 1 }}
          >
            <option value="">No crew</option>
            {crews.map((c) => (
              <option key={c.crewId} value={c.crewId}>{c.name}</option>
            ))}
          </select>
        </Row>
      </div>
      <div style={{ padding: '8px 11px 11px' }}>
        <Row gap={6}>
          <Btn
            v="ghost"
            size="sm"
            disabled={busy || who.me}
            onClick={() => onGift(who.playerId)}
            style={{ flex: 1 }}
          >
            Gift Nerve
          </Btn>
          <Btn
            v="gold"
            size="sm"
            disabled={busy || who.me || vouchLocked !== null}
            title={vouchLocked ?? undefined}
            onClick={() => onVouch(who.playerId)}
            style={{ flex: 1 }}
          >
            Vouch
          </Btn>
        </Row>
        {/* The server's sentence, verbatim. It is the only thing that knows why. */}
        {vouchLocked !== null && (
          <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 5, lineHeight: 1.35 }}>
            {vouchLocked}
          </div>
        )}
        <Btn
          v="danger"
          size="sm"
          full
          disabled={busy || who.me}
          onClick={() => onKick(who.playerId)}
          style={{ marginTop: 6 }}
        >
          Remove From Family
        </Btn>
      </div>
    </Panel>
  )
}

function MembersTab({
  o,
  roster,
  sel,
  sponsor,
  busy,
  now,
  onSel,
  onRole,
  onCrew,
  onGift,
  onVouch,
  onKick,
  onPermission,
}: {
  o: FamilyOverview
  roster: Member[]
  sel: string | null
  sponsor: SponsorState
  busy: boolean
  now: number
  onSel: (id: string) => void
  onRole: (playerId: string, role: FamilyRole) => void
  onCrew: (playerId: string, crewId: string | null) => void
  onGift: (playerId: string) => void
  onVouch: (playerId: string) => void
  onKick: (playerId: string) => void
  onPermission?: (playerId: string, permission: FamilyPermission, grant: boolean) => void
}) {
  const [q, setQ] = useState('')
  const [role, setRole] = useState<'all' | FamilyRole>('all')
  const [sort, setSort] = useState<'weekly' | 'level' | 'xp'>('weekly')

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const f = roster.filter(
      (m) =>
        (role === 'all' || m.role === role) &&
        (needle === '' ||
          m.name.toLowerCase().includes(needle) ||
          m.handle.toLowerCase().includes(needle) ||
          m.crew.toLowerCase().includes(needle)),
    )
    return [...f].sort((a, b) => b[sort] - a[sort])
  }, [roster, q, role, sort])

  const who = roster.find((m) => m.playerId === sel) ?? rows[0] ?? roster[0]

  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
      <Panel style={{ flex: 1, minWidth: 0 }}>
        <PanelHead
          title="Full Roster"
          icon="families"
          sub={`${rows.length} shown`}
          right={
            <Row gap={5}>
              {(['weekly', 'level', 'xp'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSort(s)}
                  className="t-head"
                  style={{
                    padding: '2px 7px',
                    fontSize: sz(8.5),
                    letterSpacing: '.14em',
                    color: sort === s ? 'var(--gold)' : 'var(--ink-dim)',
                    background: sort === s ? 'rgba(214,168,75,.12)' : 'transparent',
                    border: `1px solid ${sort === s ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              ))}
            </Row>
          }
        />
        <Row gap={6} style={{ padding: '7px 10px', borderBottom: '1px solid var(--line-soft)' }}>
          <Row gap={5} style={{ flex: 'none' }}>
            <Icon n="search" s={12} c="var(--ink-dim)" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or crew"
              style={{ ...inputStyle, width: 190 }}
            />
          </Row>
          <div style={{ flex: 1 }} />
          <Row gap={4} style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(['all', ...FAMILY_ROLES] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className="t-head"
                style={{
                  padding: '3px 8px',
                  fontSize: sz(9),
                  letterSpacing: '.12em',
                  color: role === r ? 'var(--gold)' : 'var(--ink-dim)',
                  background: role === r ? 'rgba(214,168,75,.12)' : 'rgba(255,255,255,.02)',
                  border: `1px solid ${role === r ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                  cursor: 'pointer',
                }}
              >
                {r === 'all' ? 'All' : ROLE_LABEL[r]}
              </button>
            ))}
          </Row>
        </Row>
        <div className="scroll-y" style={{ flex: 1 }}>
          <RosterTable rows={rows} sel={sel} onSel={onSel} now={now} />
        </div>
      </Panel>

      {who ? (
        <Dossier
          who={who}
          crews={o.crews}
          vouchLocked={sponsor.lockedReason}
          busy={busy}
          now={now}
          onRole={onRole}
          onCrew={onCrew}
          onGift={onGift}
          onVouch={onVouch}
          onKick={onKick}
          onPermission={onPermission}
        />
      ) : (
        <Panel style={{ width: 290, flex: 'none' }}>
          <PanelHead title="Member Dossier" icon="contracts" accent="var(--gold)" />
          <Empty icon="families" head="Nobody selected" sub="Pick a name off the roster." />
        </Panel>
      )}
    </Row>
  )
}

/**
 * Territory, honestly.
 *
 * Every number here is a real column the family object already keeps, and every one of them is
 * zero until Phase 7 puts blocks on the board. The mock rendered the client-side DISTRICTS list
 * instead, which showed twelve districts with invented ownership labelled Marchetti Crew.
 */
function TerritoryTab({ o }: { o: FamilyOverview }) {
  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
      <Panel style={{ flex: 1, minWidth: 0 }}>
        <PanelHead title="Territory Holdings" icon="territory" sub="Ground and what it pays" />
        <div style={{ padding: '12px 14px' }}>
          <Row gap={22} style={{ flexWrap: 'wrap' }}>
            <Stat label="Blocks held" value={o.blocksHeld} icon="territory" />
            <Stat label="Income a day" value={exact(o.incomeDaily)} c="var(--dirty)" />
            <Stat label="Upkeep a day" value={exact(o.upkeepDaily)} c="var(--arrest)" />
            <Stat
              label="Net a day"
              value={exact(o.netDaily)}
              c={o.netDaily >= 0 ? 'var(--clean)' : 'var(--arrest)'}
            />
            <Stat label="Books" value={o.books} icon="ledger" />
          </Row>
          <div className="rule" style={{ margin: '14px 0' }} />
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-mid)', lineHeight: 1.55, maxWidth: 620 }}>
            The ground opens in Phase 7. Until blocks can be taken, held and lost, these read zero,
            because they are the family's own columns rather than a picture of one.
          </p>
        </div>
      </Panel>
      {/*
        * "The Table" rather than "Standing", because the rank panel is also called Standing and
        * the two rendered side by side. This one counts the family: its Power, its seats and how
        * many of them somebody vouched for. The other one is the player's own rung.
        */}
      <Panel style={{ width: 260, flex: 'none' }}>
        <PanelHead title="The Table" icon="families" accent="var(--gold)" />
        <div style={{ padding: '10px 11px' }}>
          <Stat label="Family power" value={o.power.toLocaleString('en-US')} c="var(--gold)" />
          <div className="rule" style={{ margin: '9px 0' }} />
          <Row style={{ justifyContent: 'space-between' }}>
            <Stat label="At the table" value={`${o.memberCount} / ${o.memberCap}`} />
            <Stat label="Sponsored" value={o.sponsoredCount} align="right" />
          </Row>
          <div className="rule" style={{ margin: '9px 0' }} />
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-faint)', lineHeight: 1.45 }}>
            Power is the sum of every member's own Power. It is not a league position.
          </p>
        </div>
      </Panel>
    </Row>
  )
}

/**
 * The vault: two tanks, the goods, and the ledger that says who moved what.
 *
 * The mock's "302 of 420 slots" and "Level 4 vault" are both gone. There is no slot count and
 * there are no vault levels: capacity is a cash cap in cents and the server publishes it.
 */
function VaultTab({ o, vault, log, now }: { o: FamilyOverview; vault: VaultItem[]; log: TreasuryEntry[]; now: number }) {
  const held = (o.treasuryDirty + o.treasuryClean) as Cents
  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
      <Panel style={{ width: 300, flex: 'none', overflow: 'hidden' }}>
        <PanelHead title="The Vault" icon="lock" accent="var(--clean)" />
        <Art
          src={A('families.vault_interior')}
          alt="Converted bank vault beneath the social club"
          scrim="soft"
          style={{ height: 150, flex: 'none' }}
        />
        <div style={{ padding: '10px 11px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <Stat label="Dirty held" value={exact(o.treasuryDirty)} c="var(--dirty)" />
            <Stat label="Clean held" value={exact(o.treasuryClean)} c="var(--clean)" align="right" />
          </Row>
          <div className="rule" style={{ margin: '10px 0' }} />
          <div className="t-label" style={{ fontSize: sz(9) }}>Against the cap</div>
          <div style={{ marginTop: 5 }}>
            <Meter v={held} max={o.treasuryCap} a="var(--brass-deep)" b="var(--clean)" h={5} />
          </div>
          <Row style={{ marginTop: 4, justifyContent: 'space-between' }}>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>{exact(held)}</span>
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--gold)' }}>{exact(o.treasuryCap)}</span>
          </Row>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 10, lineHeight: 1.4 }}>
            The tithe fills the dirty tank. Every family sink spends the clean one.
          </div>
        </div>
      </Panel>
      <Panel style={{ flex: 1, minWidth: 0 }}>
        <PanelHead title="Vault Inventory" icon="crate" sub={`${vault.length} lines held`} />
        <div className="scroll-y" style={{ flex: 1, padding: 9 }}>
          {vault.length === 0 ? (
            <Empty
              icon="crate"
              head="Nothing in the box"
              sub="Goods put up for the family show here with a note on where they came from."
            />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 7 }}>
              {vault.map((v) => (
                <Row
                  key={v.itemId}
                  gap={9}
                  style={{
                    padding: '8px 9px',
                    border: '1px solid var(--line)',
                    background: 'linear-gradient(180deg, rgba(255,255,255,.03), rgba(8,11,13,.6))',
                  }}
                >
                  <Art
                    src={art(v.icon, 'items.materials')}
                    alt={v.name}
                    scrim="none"
                    style={{ width: 34, height: 34, flex: 'none', border: '1px solid var(--line-soft)' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Row gap={6}>
                      <span
                        style={{
                          fontSize: 11.5,
                          color: 'var(--ink-hi)',
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {v.name}
                      </span>
                      <span className="t-num" style={{ fontSize: 13, color: v.c }}>{v.qty}</span>
                    </Row>
                    <div style={{ fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.3 }}>{v.note}</div>
                  </div>
                </Row>
              ))}
            </div>
          )}
        </div>
      </Panel>
      <Panel style={{ width: 300, flex: 'none' }}>
        <PanelHead title="Movement" icon="ledger" sub="Who moved what" />
        <div className="scroll-y" style={{ flex: 1, padding: '6px 10px 10px' }}>
          {log.length === 0 ? (
            <Empty icon="ledger" head="No movement" sub="Every cent in and out writes a line, with a name on it, forever." />
          ) : (
            <Col gap={0}>
              {log.map((e) => (
                <Row key={e.id} gap={8} style={{ padding: '6px 0', borderBottom: '1px solid var(--line-soft)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: 'var(--ink-hi)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {e.who}
                    </div>
                    <div className="t-label" style={{ fontSize: sz(8) }}>
                      {e.reason} &middot; {ago(e.at, now)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flex: 'none' }}>
                    <div
                      className="t-num"
                      style={{ fontSize: 12, color: e.delta >= 0 ? 'var(--dirty)' : 'var(--arrest)' }}
                    >
                      {e.delta >= 0 ? '+' : '-'}
                      {money(Math.abs(e.delta) as Cents)}
                    </div>
                    <div className="t-label" style={{ fontSize: sz(8) }}>
                      {e.currency} at {money(e.balance)}
                    </div>
                  </div>
                </Row>
              ))}
            </Col>
          )}
        </div>
      </Panel>
    </Row>
  )
}

/**
 * Four branches, and a family takes two.
 *
 * The price on the card goes back up as `expectCost`, so the published price is the price the
 * server executes at or it refuses. Affordability is measured against the CLEAN tank, because
 * that is the tank this sink spends.
 */
function UpgradesTab({
  o,
  upgrades,
  busy,
  onBuy,
}: {
  o: FamilyOverview
  upgrades: Upgrade[]
  busy: boolean
  onBuy: (u: Upgrade) => void
}) {
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="Family Upgrades"
        icon="rank"
        sub="Two branches for the Era"
        right={
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--clean)' }}>
            {exact(o.treasuryClean)} clean available
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: 9 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 8 }}>
          {upgrades.map((u) => {
            const broke = u.cost > o.treasuryClean
            const stuck = u.lockedReason !== null
            return (
              <div
                key={u.upgradeId}
                style={{
                  padding: '9px 11px',
                  border: `1px solid ${u.chosen ? 'var(--line-brass)' : 'var(--line)'}`,
                  background: u.chosen
                    ? 'linear-gradient(180deg, rgba(214,168,75,.1), rgba(8,11,13,.7))'
                    : 'linear-gradient(180deg, rgba(255,255,255,.03), rgba(8,11,13,.7))',
                }}
              >
                <Row gap={9}>
                  <Icon n={glyph(u.icon)} s={17} c={u.c} />
                  <span
                    className="t-head"
                    style={{ fontSize: 11.5, color: 'var(--ink-hi)', letterSpacing: '.08em', flex: 1 }}
                  >
                    {u.name}
                  </span>
                  {u.chosen && <Chip c="var(--gold)">Taken</Chip>}
                  <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--gold)' }}>
                    Lv {u.lvl} / {u.max}
                  </span>
                </Row>
                <div style={{ marginTop: 6 }}>
                  <Meter v={u.lvl} max={u.max} a="var(--brass-deep)" b={u.c} h={4} />
                </div>
                <p style={{ margin: '7px 0 0', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.4 }}>{u.effect}</p>
                {u.nextEffect !== null && (
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.4 }}>
                    Next: {u.nextEffect}
                  </p>
                )}
                {/* Verbatim. The server is the only thing that knows which two branches are gone. */}
                {u.lockedReason !== null && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--arrest)', lineHeight: 1.4 }}>
                    {u.lockedReason}
                  </div>
                )}
                <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
                  <div>
                    <div className="t-label" style={{ fontSize: sz(8.5) }}>Next level, {u.currency} cash</div>
                    <div className="t-num" style={{ fontSize: 13, color: 'var(--clean)' }}>
                      {u.cost === 0 ? 'nothing left to buy' : exact(u.cost)}
                    </div>
                  </div>
                  <Btn
                    v={!stuck && !broke ? 'gold' : 'ghost'}
                    size="sm"
                    disabled={busy || stuck || broke}
                    onClick={() => onBuy(u)}
                  >
                    {stuck ? 'Locked' : broke ? 'Short On Funds' : u.chosen ? 'Fund Next Level' : 'Take This Branch'}
                  </Btn>
                </Row>
              </div>
            )
          })}
        </div>
      </div>
    </Panel>
  )
}

/**
 * The war log, which is empty until Phase 7 fights the first one.
 *
 * No Standing Order card, no countdown to a window that does not open and no Declare War button
 * wired to nothing. When wars exist, the rows below are what the server already records.
 */
function WarLogTab({ rows, o }: { rows: WarEntry[]; o: FamilyOverview }) {
  return (
    <Row gap={8} className="cols" style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
      <Panel style={{ flex: 1, minWidth: 0 }}>
        <PanelHead title="War Log" icon="warn" accent="var(--arrest)" sub={`${rows.length} recorded`} />
        <div className="scroll-y" style={{ flex: 1 }}>
          {rows.length === 0 ? (
            <Empty
              icon="fist"
              head="No wars fought"
              sub="Wars open in Phase 7. Every engagement writes a row here with the ground, the take and what it cost."
            />
          ) : (
            <table className="grid-tbl">
              <thead>
                <tr>
                  {['When', 'Opponent', 'Ground', 'Result', 'Net Take', 'Ours Down'].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        background: 'var(--panel)',
                        textAlign: i >= 4 ? 'right' : 'left',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.warId}>
                    <td className="t-mono" style={{ color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>{w.when}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--ink-hi)', whiteSpace: 'nowrap' }}>{w.foe}</td>
                    <td style={{ fontSize: 11, color: 'var(--ink-mid)', whiteSpace: 'nowrap' }}>{w.districtId}</td>
                    <td>
                      <Chip c={RESULT_C[w.result]}>{RESULT_LABEL[w.result]}</Chip>
                    </td>
                    <td
                      className="t-num"
                      style={{
                        textAlign: 'right',
                        color: w.take > 0 ? 'var(--dirty)' : w.take < 0 ? 'var(--arrest)' : 'var(--ink-dim)',
                      }}
                    >
                      {w.take === 0 ? 'nothing' : exact(w.take)}
                    </td>
                    <td className="t-num" style={{ textAlign: 'right', color: w.lost ? 'var(--heat)' : 'var(--ink-dim)' }}>
                      {w.lost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
      <Panel style={{ width: 260, flex: 'none' }}>
        <PanelHead title="Record" icon="fist" accent="var(--heat)" />
        <div style={{ padding: '10px 11px' }}>
          <Row style={{ justifyContent: 'space-between' }}>
            <Stat label="Won" value={o.warWins} c="var(--dirty)" />
            <Stat label="Lost" value={o.warLosses} c="var(--arrest)" align="right" />
          </Row>
          <div className="rule" style={{ margin: '10px 0' }} />
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            Two counters the family object has kept since it was founded. Nothing can move them
            until wars open.
          </p>
        </div>
      </Panel>
    </Row>
  )
}

/* ----------------------------------------------------------------- browser */

function BrowseRow({ f, on, onPick }: { f: FamilySummary; on: boolean; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        width: '100%',
        padding: '8px 9px',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        border: `1px solid ${on ? 'var(--line-brass)' : 'var(--line)'}`,
        background: on
          ? 'linear-gradient(180deg, rgba(214,168,75,.12), rgba(8,11,13,.7))'
          : 'linear-gradient(180deg, rgba(255,255,255,.03), rgba(8,11,13,.7))',
      }}
    >
      <Crest crestId={f.crestId} name={f.name} size={46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Row gap={7}>
          <span
            className="t-head"
            style={{
              fontSize: 12.5,
              color: 'var(--ink-hi)',
              letterSpacing: '.05em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {f.name}
          </span>
          <Chip c="var(--brass-lit)">{f.tag}</Chip>
        </Row>
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-dim)',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {f.motto === '' ? 'No motto over the door.' : f.motto}
        </div>
        <Row gap={12} style={{ marginTop: 3, flexWrap: 'wrap' }}>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-mid)' }}>
            {f.memberCount} / {f.memberCap} at the table
          </span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-mid)' }}>
            Level {f.minLevel} and up
          </span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--gold)' }}>
            Power {f.power.toLocaleString('en-US')}
          </span>
          <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-mid)' }}>
            {f.blocksHeld} blocks
          </span>
          <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>Boss {f.bossName}</span>
        </Row>
        {/* The server says why, in its own words. A padlock is not a reason. */}
        {f.lockedReason !== null && (
          <div style={{ fontSize: sz(10.5), color: 'var(--arrest)', marginTop: 3, lineHeight: 1.35 }}>
            {f.lockedReason}
          </div>
        )}
      </div>
    </button>
  )
}

/**
 * The anonymous offer.
 *
 * `sponsorId` and `sponsorName` are deliberately null until acceptance: the sponsee sees a blurb
 * naming a rank and a district and learns who it was only on taking it. Accepting needs a
 * WITNESS, and a witness is a third party, so the picker is built out of the people this payload
 * actually names. When it names nobody the id box is the honest fallback, because inventing a
 * list of candidates would be inventing players.
 */
function SponsorOffer({
  sponsor,
  busy,
  now,
  candidates,
  onAccept,
}: {
  sponsor: SponsorState
  busy: boolean
  now: number
  candidates: { id: string; name: string }[]
  onAccept: (id: string, witnessId: string) => void
}) {
  const [witness, setWitness] = useState(candidates[0]?.id ?? '')
  const offer = sponsor.incoming
  if (offer === null) return null
  return (
    <Panel tone="gold" style={{ flex: 'none' }}>
      <PanelHead title="Somebody Vouched" icon="rep" accent="var(--gold)" />
      <div style={{ padding: '9px 11px 11px' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink)', lineHeight: 1.5 }}>{offer.blurb}</p>
        <Row gap={14} style={{ marginTop: 8, flexWrap: 'wrap' }}>
          <Stat label="Their stake" value={exact(offer.stake)} c="var(--clean)" />
          <Stat label="Offered" value={ago(offer.offeredAt, now)} />
          <Stat label="Lapses in" value={clock((offer.expiresAt - now) / 1000)} c="var(--gold)" />
        </Row>
        <div className="rule" style={{ margin: '10px 0' }} />
        <div className="t-label" style={{ fontSize: sz(9) }}>Witness</div>
        {candidates.length === 0 ? (
          <input
            value={witness}
            onChange={(e) => setWitness(e.target.value)}
            placeholder="Player id of your witness"
            style={{ ...inputStyle, width: '100%', marginTop: 5 }}
          />
        ) : (
          <select
            value={witness}
            onChange={(e) => setWitness(e.target.value)}
            style={{ ...selectStyle, width: '100%', marginTop: 5 }}
          >
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 4, lineHeight: 1.35 }}>
          A witness is somebody else. That is the whole point of one.
        </div>
        <Btn
          v="gold"
          size="sm"
          full
          disabled={busy || witness.trim() === ''}
          onClick={() => onAccept(offer.id, witness.trim())}
          style={{ marginTop: 8 }}
        >
          Take The Vouch
        </Btn>
      </div>
    </Panel>
  )
}

/** The two sides of the Line, with the exact numbers, and the one sentence that makes it a choice. */
export function LineChoice({ busy, onLine }: { busy: boolean; onLine: (line: 'energy' | 'nerve') => void }) {
  return (
    <div style={{ marginTop: 10, padding: '9px 10px 10px', border: '1px solid var(--line-brass)', background: 'color-mix(in srgb, var(--gold) 6%, transparent)' }}>
      <div className="t-head" style={{ fontSize: sz(12), color: 'var(--gold)', letterSpacing: '.1em' }}>
        Draw the Line
      </div>
      <div style={{ fontSize: sz(11), color: 'var(--ink-mid)', lineHeight: 1.45, marginTop: 3 }}>
        Every Soldier draws it once. It holds for the Era and it does not move.
      </div>
      <Col gap={6} style={{ marginTop: 8 }}>
        <Row gap={8} style={{ alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: sz(12), color: 'var(--ink-hi)' }}>{LINE_NAME.energy}</div>
            <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', lineHeight: 1.4 }}>
              Your Energy bar runs {LINE_ENERGY_BP / 100} percent longer. More jobs, more crimes, every bar.
            </div>
          </div>
          <Btn v="gold" size="sm" disabled={busy} onClick={() => onLine('energy')} id="line-energy" style={{ flex: 'none' }}>
            Take it
          </Btn>
        </Row>
        <Row gap={8} style={{ alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: sz(12), color: 'var(--ink-hi)' }}>{LINE_NAME.nerve}</div>
            <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', lineHeight: 1.4 }}>
              {LINE_NERVE_POINTS} more points of Nerve. The harder crimes, and the fights, come sooner.
            </div>
          </div>
          <Btn v="gold" size="sm" disabled={busy} onClick={() => onLine('nerve')} id="line-nerve" style={{ flex: 'none' }}>
            Take it
          </Btn>
        </Row>
      </Col>
    </div>
  )
}

/**
 * The rung, the checklist and the floor. GAME-DESIGN 6.1 and 6.3.
 *
 * 6.3 makes this a screen rather than a boolean and says why: "An unsponsored player at day 7 is
 * capped out of half the game and knows it, BECAUSE THE RANK SCREEN SHOWS THE CHECKLIST." Every
 * condition is drawn whether it is met or not, with the number the server counted and the number
 * it wanted, in the order 6.1 states them.
 *
 * The house is drawn last and never first. It is deliberately worse than a human vouch in four
 * ways and it writes a permanent Rap Sheet line nobody wants, so the panel says both before the
 * button, and a player who can get a person to speak for them should.
 */
function RankLadder({
  rank,
  busy,
  onClaim,
  onTakeHouse,
  onLine,
}: {
  rank: RankState | null
  busy: boolean
  onClaim: () => void
  onTakeHouse: () => void
  onLine: (line: 'energy' | 'nerve') => void
}) {
  if (rank === null) return null
  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead
        title="Standing"
        icon="rank"
        accent="var(--gold)"
        right={
          <Row gap={5}>
            <Chip c="var(--gold)">{rank.rank}</Chip>
            {rank.line !== '' && <Chip c="var(--brass-lit)">{LINE_NAME[rank.line]}</Chip>}
          </Row>
        }
      />
      <div style={{ padding: '9px 11px 11px' }}>
        {rank.nextRank === null ? (
          <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.45 }}>
            Capo is as far as anybody earns it. The rest are chairs.
          </div>
        ) : (
          <>
            <div className="t-label" style={{ fontSize: sz(9) }}>What {rank.nextRank} costs</div>
            <Col gap={5} style={{ marginTop: 6 }}>
              {rank.requirements.map((r: RankRequirement) => (
                <Row key={r.id} gap={8} style={{ alignItems: 'baseline' }}>
                  {/*
                    * A DEFERRED CONDITION IS NOT A LOCKED ONE, and it used to draw the same padlock.
                    *
                    * A deferred condition is stated and never counted: it does not block the rung.
                    * Drawing it with the icon that means "this is what is stopping you" told the
                    * player the opposite of what the server does, and the only thing separating the
                    * two was a slightly fainter grey.
                    */}
                  <Icon
                    n={r.met ? 'check' : r.deferred ? 'info' : 'lock'}
                    s={11}
                    c={r.met ? 'var(--clean)' : r.deferred ? 'var(--ink-faint)' : 'var(--ink-dim)'}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 11.5,
                      color: r.met ? 'var(--ink)' : 'var(--ink-mid)',
                    }}
                  >
                    {r.label}
                  </span>
                  <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-faint)' }}>
                    {/*
                      * "not yet built" is a development status and the player is not the developer.
                      * What is true for them is that this one is not being counted against them.
                      */}
                    {r.deferred
                      ? 'not counted'
                      : r.have === null || r.need === null
                        ? ''
                        : r.id === 'clean_held'
                          ? `${money(cents(r.have))} / ${money(cents(r.need))}`
                          : `${r.have} / ${r.need}`}
                  </span>
                </Row>
              ))}
            </Col>
            <Btn
              v="gold"
              size="sm"
              full
              disabled={busy || !rank.canPromote}
              onClick={onClaim}
              style={{ marginTop: 9 }}
            >
              {rank.canPromote ? `Take ${rank.nextRank}` : 'Not Yet'}
            </Btn>
          </>
        )}

        {/*
          * PHASE 24. THE LINE, the Soldier ceremony's one question. Asked once, answered once, and
          * the answer sits on the Standing chip and the Street row for the Era. DESIGN-DELTAS 1ae.1.
          */}
        {rank.lineOpen && <LineChoice busy={busy} onLine={onLine} />}

        {rank.vouched && rank.vouchOutcome !== '' && (
          <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 8, lineHeight: 1.4 }}>
            On your Rap Sheet, permanently: {rank.vouchOutcome}
          </div>
        )}

        {rank.houseOffer !== null && (
          <>
            <div className="rule" style={{ margin: '10px 0' }} />
            <Row gap={8} style={{ alignItems: 'center' }}>
              <Art
                src={A(rank.houseOffer.portraitId)}
                alt={rank.houseOffer.name}
                scrim="none"
                style={{ width: 38, height: 38, flex: 'none', border: '1px solid var(--line-brass)' }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{rank.houseOffer.name}</div>
                <div className="t-label" style={{ fontSize: sz(8.5) }}>{rank.houseOffer.seat}</div>
              </div>
            </Row>
            <p style={{ margin: '7px 0 0', fontSize: 11.5, color: 'var(--ink)', lineHeight: 1.45 }}>
              {rank.houseOffer.blurb}
            </p>
            {/*
              * The authored line, from `content/house.json`. What stood here was a paragraph typed
              * into this component while the authored one shipped in the bundle unread, and it
              * advertised a co-active Heat bonus ("no shared cover") that exists nowhere in the
              * tree: a reward named by negation is still a reward named, and the standing ruling is
              * that a displayed reward with no payout is removed rather than deferred.
              */}
            <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 5, lineHeight: 1.4 }}>
              {rank.houseOffer.terms}
            </div>
            <Btn v="ghost" size="sm" full disabled={busy} onClick={onTakeHouse} style={{ marginTop: 8 }}>
              Take The House Vouch
            </Btn>
          </>
        )}
        {/*
          * `!rank.vouched` was here, and it was the exact complement of the one condition that
          * produces `house.held`.
          *
          * The server emits `held` when and only when `vouches > 0`, and `vouched` is that same
          * number on the same read. So the string authored for the case the whole mechanic wants,
          * "Somebody already spoke for you. The house does not speak twice.", was the one string
          * on this panel that could never appear. Four locked reasons, three reachable.
          *
          * The guard it needed was the one it now has: show the reason whenever there is a reason
          * and no offer. `houseLocked` is null exactly when `houseOffer` is not.
          */}
        {rank.houseOffer === null && rank.houseLocked !== null && (
          <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)', marginTop: 8, lineHeight: 1.4 }}>
            {rank.houseLocked}
          </div>
        )}
      </div>
    </Panel>
  )
}

function Charter({ busy, onFound }: { busy: boolean; onFound: (name: string, tag: string, motto: string) => void }) {
  const [name, setName] = useState('')
  const [tag, setTag] = useState('')
  const [motto, setMotto] = useState('')
  const tagOk = tag.length >= FAMILY_TAG_MIN && tag.length <= FAMILY_TAG_MAX
  const nameOk = name.trim().length >= 3

  return (
    <Panel style={{ flex: 'none' }}>
      <PanelHead title="Put Your Own Name Over A Door" icon="families" accent="var(--gold)" />
      <Col gap={6} style={{ padding: '9px 11px 11px' }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Family name"
          style={{ ...inputStyle, width: '100%' }}
        />
        <Row gap={6}>
          <input
            value={tag}
            onChange={(e) => setTag(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, FAMILY_TAG_MAX))}
            placeholder="TAG"
            style={{ ...inputStyle, width: 84, textAlign: 'center' }}
          />
          <span className="t-label" style={{ fontSize: sz(8.5), flex: 1 }}>
            {FAMILY_TAG_MIN} to {FAMILY_TAG_MAX} letters or numbers
          </span>
        </Row>
        <input
          value={motto}
          onChange={(e) => setMotto(e.target.value)}
          placeholder="Motto, if you have one"
          style={{ ...inputStyle, width: '100%' }}
        />
        <Btn
          v="gold"
          size="sm"
          full
          icon="plus"
          disabled={busy || !nameOk || !tagOk}
          onClick={() => onFound(name.trim(), tag, motto.trim())}
        >
          Charter The Family
        </Btn>
      </Col>
    </Panel>
  )
}

/**
 * The screen a player with no family sees.
 *
 * Every row is an Era read model summary, and the reason a door is shut is the server's sentence
 * rendered verbatim: a padlock says no without saying why, and why is the part that tells
 * somebody what to do next.
 */
function Browser({
  browse,
  mine,
  sponsor,
  rank,
  busy,
  now,
  onApply,
  onFound,
  onAccept,
  onClaim,
  onTakeHouse,
  onLine,
}: {
  browse: FamilySummary[]
  mine: { familyId: string; name: string; appliedAt: number; expiresAt: number } | null
  sponsor: SponsorState
  rank: RankState | null
  busy: boolean
  now: number
  onApply: (familyId: string, pitch: string) => void
  onFound: (name: string, tag: string, motto: string) => void
  onAccept: (id: string, witnessId: string) => void
  onClaim: () => void
  onTakeHouse: () => void
  onLine: (line: 'energy' | 'nerve') => void
}) {
  const { stacked } = useViewport()
  const [sel, setSel] = useState<string | null>(null)
  const [pitch, setPitch] = useState('')
  const picked = browse.find((f) => f.familyId === sel) ?? null

  // Everyone this payload names by id, which for an unaffiliated player is whoever they have
  // already vouched for. There is no player directory on this read.
  const candidates = useMemo(() => {
    const out = new Map<string, string>()
    for (const s of sponsor.open) {
      if (s.sponseeId !== '') out.set(s.sponseeId, s.sponseeName)
      if (s.witnessId !== null) out.set(s.witnessId, s.witnessName)
    }
    return [...out].map(([id, name]) => ({ id, name }))
  }, [sponsor.open])

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '9px 12px 10px', alignItems: 'flex-start' }}>
            <div>
              <Row gap={9}>
                <span className="t-display" style={{ fontSize: 27, color: 'var(--ink-hi)', letterSpacing: '.06em' }}>
                  Families
                </span>
                <span style={{ fontSize: 12, color: 'var(--ink-dim)', fontStyle: 'italic' }}>
                  Nobody gets far in this city on their own.
                </span>
              </Row>
              <div style={{ fontSize: 11.5, color: 'var(--ink-mid)', marginTop: 6 }}>
                {browse.length} crews taking names this Era.
              </div>
            </div>
          </Row>
        </Panel>

        <Panel style={{ flex: 1, minHeight: 0 }}>
          <PanelHead title="Open Doors" icon="families" sub={`${browse.length} listed`} />
          <div className="scroll-y" style={{ flex: 1, padding: 9 }}>
            {browse.length === 0 ? (
              <Empty
                icon="families"
                head="No crews yet"
                sub="Nobody has chartered anything this Era. That is an opening."
              />
            ) : (
              <Col gap={7}>
                {browse.map((f) => (
                  <BrowseRow key={f.familyId} f={f} on={sel === f.familyId} onPick={() => setSel(f.familyId)} />
                ))}
              </Col>
            )}
          </div>
        </Panel>
      </Col>

      <Rail width={336}>
        <SponsorOffer sponsor={sponsor} busy={busy} now={now} candidates={candidates} onAccept={onAccept} />
        <RankLadder rank={rank} busy={busy} onClaim={onClaim} onTakeHouse={onTakeHouse} onLine={onLine} />

        {mine !== null && (
          <Panel style={{ flex: 'none' }}>
            <PanelHead title="You Knocked" icon="mail" accent="var(--gold)" />
            <div style={{ padding: '9px 11px 11px' }}>
              <div style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{mine.name}</div>
              <Row gap={14} style={{ marginTop: 6, justifyContent: 'space-between' }}>
                <Stat label="Asked" value={ago(mine.appliedAt, now)} />
                <Stat label="Lapses in" value={clock((mine.expiresAt - now) / 1000)} c="var(--gold)" align="right" />
              </Row>
            </div>
          </Panel>
        )}

        <Panel style={{ flex: 'none' }}>
          <PanelHead title="Ask In" icon="mail" />
          <div style={{ padding: '9px 11px 11px' }}>
            {picked === null ? (
              <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', lineHeight: 1.45 }}>
                Pick a crew on the left and say why they should want you.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: 'var(--ink-hi)' }}>{picked.name}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: 2 }}>Boss {picked.bossName}</div>
                {picked.lockedReason !== null && (
                  <div style={{ fontSize: 11, color: 'var(--arrest)', marginTop: 6, lineHeight: 1.4 }}>
                    {picked.lockedReason}
                  </div>
                )}
                <textarea
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value)}
                  placeholder="What you bring, in your own words"
                  rows={4}
                  style={{ ...inputStyle, width: '100%', marginTop: 7, resize: 'vertical', lineHeight: 1.4 }}
                />
                <Btn
                  v="gold"
                  size="sm"
                  full
                  disabled={busy || picked.lockedReason !== null || pitch.trim() === ''}
                  onClick={() => onApply(picked.familyId, pitch.trim())}
                  style={{ marginTop: 7 }}
                >
                  Apply
                </Btn>
              </>
            )}
          </div>
        </Panel>

        <Charter busy={busy} onFound={onFound} />
      </Rail>
    </Stack>
  )
}

/* ------------------------------------------------------------------- screen */

export function Families() {
  const { now } = useGame()
  const { stacked } = useViewport()

  // PER-FIELD selectors, not a whole-store destructure: socket frames patch this slice several
  // times a second and a store-wide subscription re-renders the entire screen on every one.
  const family = useSession((s) => s.family)
  const busy = useSession((s) => s.busy)
  const refusal = useSession((s) => s.refusal)
  const clearRefusal = useSession((s) => s.clearRefusal)
  const notice = useSession((s) => s.notice)
  const clearNotice = useSession((s) => s.clearNotice)
  const loadFamily = useSession((s) => s.loadFamily)
  const joinRoom = useSession((s) => s.joinRoom)
  const leaveRoom = useSession((s) => s.leaveRoom)
  const roomStatus = useSession((s) => s.roomStatus)
  const createFamily = useSession((s) => s.createFamily)
  const applyToFamily = useSession((s) => s.applyToFamily)
  const decideApplication = useSession((s) => s.decideApplication)
  const setFamilyRole = useSession((s) => s.setFamilyRole)
  const setFamilyCrew = useSession((s) => s.setFamilyCrew)
  const kickMember = useSession((s) => s.kickMember)
  const grantPermission = useSession((s) => s.grantPermission)
  const leaveFamily = useSession((s) => s.leaveFamily)
  const depositToVault = useSession((s) => s.depositToVault)
  const withdrawFromVault = useSession((s) => s.withdrawFromVault)
  const buyFamilyUpgrade = useSession((s) => s.buyFamilyUpgrade)
  const setTithe = useSession((s) => s.setTithe)
  const setFamilySettings = useSession((s) => s.setFamilySettings)
  const postFamilyChat = useSession((s) => s.postFamilyChat)
  const announce = useSession((s) => s.announce)
  const giftNerve = useSession((s) => s.giftNerve)
  const offerVouch = useSession((s) => s.offerVouch)
  const acceptVouch = useSession((s) => s.acceptVouch)
  const rank = useSession((s) => s.rank)
  const loadRank = useSession((s) => s.loadRank)
  const claimRank = useSession((s) => s.claimRank)
  const takeHouseVouch = useSession((s) => s.takeHouseVouch)
  const drawLine = useSession((s) => s.drawLine)

  const [tab, setTab] = useState<TabId>('overview')
  const [sel, setSel] = useState<string | null>(null)

  // The read that renders the buttons is the read that mints their token.
  useEffect(() => {
    void loadFamily()
  }, [loadFamily])

  // The ladder is its own read with its own token bundle: a player with no family still has a
  // rung, and is exactly the player the house at Era day 10 exists for.
  useEffect(() => {
    void loadRank()
  }, [loadRank])

  // The room opens once the read says there is one, and closes on the way out. `joinRoom` takes
  // the path off the slice itself and is idempotent, so a re-render cannot open a second line.
  const socketPath = family?.socketPath ?? null
  useEffect(() => {
    joinRoom()
    return () => leaveRoom()
  }, [socketPath, joinRoom, leaveRoom])

  /**
   * A composer clears its box only when the post LANDED. The store swallows the failure into
   * `refusal`, so the absence of a fresh one is what success looks like from out here.
   */
  const sent = async (run: Promise<void>): Promise<boolean> => {
    await run
    return useSession.getState().refusal === null
  }

  if (family === null) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--ink-faint)' }}>
        <span className="t-head" style={{ letterSpacing: '.24em', fontSize: 11 }}>
          READING THE BOOK
        </span>
      </div>
    )
  }

  if (family.overview === null) {
    return (
      <Col gap={0} style={{ flex: 1, minHeight: 0 }}>
        {refusal && (
          <div style={{ padding: '8px 8px 0' }}>
            <Refusal text={refusal} onClose={clearRefusal} />
          </div>
        )}
        {notice && (
          <div style={{ padding: '8px 8px 0' }}>
            <Notice text={notice} onClose={clearNotice} />
          </div>
        )}
        <Browser
          browse={family.browse}
          mine={family.myApplication}
          sponsor={family.sponsor}
          rank={rank}
          busy={busy}
          now={now}
          onApply={(familyId, pitch) => void applyToFamily(familyId, pitch)}
          onFound={(name, tag, motto) => void createFamily(name, tag, motto)}
          onAccept={(id, witnessId) => void acceptVouch(id, witnessId)}
          onClaim={() => void claimRank()}
          onTakeHouse={() => void takeHouseVouch()}
          onLine={(line) => void drawLine(line)}
        />
      </Col>
    )
  }

  const o = family.overview

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Row gap={8} className="cols" style={{ flex: 'none', height: 178, alignItems: 'stretch' }}>
          <HeaderBand o={o} now={now} />
          <TreasuryPanel
            o={o}
            busy={busy}
            onDeposit={(cur, amount) => void depositToVault(cur, amount)}
            onWithdraw={(cur, amount) => void withdrawFromVault(cur, amount)}
          />
          <PerksPanel o={o} />
        </Row>

        <div style={{ flex: 'none' }}>
          <Tabs items={TABS.map((t) => ({ id: t.id, label: t.label }))} value={tab} onChange={setTab} />
        </div>

        {tab === 'overview' && (
          <OverviewTab
            o={o}
            roster={family.roster}
            applications={family.applications}
            announcements={family.announcements}
            busy={busy}
            now={now}
            onSel={(id) => {
              setSel(id)
              setTab('members')
            }}
            onDecide={(playerId, accept) => void decideApplication(playerId, accept)}
          />
        )}
        {tab === 'members' && (
          <MembersTab
            o={o}
            roster={family.roster}
            sel={sel}
            sponsor={family.sponsor}
            busy={busy}
            now={now}
            onSel={setSel}
            onRole={(playerId, role) => void setFamilyRole(playerId, role)}
            onCrew={(playerId, crewId) => void setFamilyCrew(playerId, crewId)}
            onGift={(playerId) => void giftNerve(playerId)}
            onVouch={(playerId) => void offerVouch(playerId)}
            onPermission={(playerId, permission, grant) =>
              void grantPermission(playerId, permission, grant)
            }
            onKick={(playerId) => void kickMember(playerId, 'Removed from the roster.')}
          />
        )}
        {tab === 'territory' && <TerritoryTab o={o} />}
        {tab === 'vault' && <VaultTab o={o} vault={family.vault} log={family.treasuryLog} now={now} />}
        {tab === 'upgrades' && (
          <UpgradesTab
            o={o}
            upgrades={family.upgrades}
            busy={busy}
            // The published price rides back up as `expectCost`, so a price that moved between the
            // read and the tap is refused rather than quietly charged.
            onBuy={(u) => void buyFamilyUpgrade(u.upgradeId, u.cost)}
          />
        )}
        {tab === 'warlog' && <WarLogTab rows={family.warLog} o={o} />}
      </Col>

      <Rail width={296}>
        {refusal && <Refusal text={refusal} onClose={clearRefusal} />}
        {notice && <Notice text={notice} onClose={clearNotice} />}
        <RankLadder
          rank={rank}
          busy={busy}
          onClaim={() => void claimRank()}
          onTakeHouse={() => void takeHouseVouch()}
          onLine={(line) => void drawLine(line)}
        />
        <FeedPanel audit={family.audit} log={family.treasuryLog} now={now} />
        <ChatPanel
          lines={family.chat}
          busy={busy}
          status={roomStatus}
          now={now}
          onSend={(body) => sent(postFamilyChat(body))}
        />
        <QuickActions
          o={o}
          busy={busy}
          onAnnounce={(body) => sent(announce(body, false))}
          onTithe={(pct) => void setTithe(pct)}
          onRecruiting={(on) => void setFamilySettings(on, o.minLevel, o.motto)}
          onLeave={() => void leaveFamily()}
        />
      </Rail>
    </Stack>
  )
}
